import { VisitPopulated } from '@api/READ_ONLY/visits_api/Api';
import TableLink from '@app/components/common/TableLink';
import { APP_PREFIX, CAREGIVERS_PREFIX, EDIT_CAREGIVER, VISITS_PAGE } from '@app/routes/urls';
import { LIST_DATE_FORMAT, LIST_TIME_FORMAT } from '@app/utils/dates/dateFormats';
import { currencyFormatter, formatPercent } from '@app/utils/pricingCurrencyUtils';
import envConfig from '@app/enviroment/enviroment';
import { formatVisitPayment, getVisitPaymentStatus } from '@feat-visits/visitPayment';
import { ClientDisplay } from '@feat-scheduling/components/VisitCalendarEvent/ClientDisplay';
import { BaseColumn, TableRow } from '@konnecta/generic-table';
import { Tag, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { DateTimeDisplay } from '../../../../components/common/DateTimeDisplay';
import { ServicesDisplay } from '../../../../components/common/ServicesDisplay';
import { getAddressWithFallback } from '../../../../utils/addressUtils';
import { clientCardPath } from '@app/features/clients/clientLinks';
import { visitStatusColor, visitStatusLabelKey } from '@feat-visits/visitStatus';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

export interface VisitRow extends Omit<VisitPopulated, 'id'>, TableRow {
  id: string | number;
}

const getStatusColor = visitStatusColor;

export interface VisitTableNavigationHandlers {
  onClientClick: (clientId: number) => void;
  onCaregiverClick: (caregiverId: number) => void;
  onVisitClick: (visitId: number | string) => void;
  /**
   * REQUIRED. Optional-with-a-default fails OPEN: a caller that forgets it shows
   * an office administrator the cost and profit columns, and every test of this
   * builder still passes because they all pass the flag by hand.
   */
  showInternalFinancials: boolean;
  /**
   * Client-facing money: price, discount, final price and the payment summary.
   * REQUIRED for the same reason as the flag above — a caller that forgets it
   * would show a doctor what the patient is charged, which the backend already
   * strips for them, leaving four columns of '-' as the only hint.
   */
  showClientPricing: boolean;
  /**
   * Where a patient's name points. Defaults to the staff client card, which is
   * where every list in the product sends it — the doctor portal overrides it,
   * because that route does not resolve for a doctor.
   */
  clientCardHref?: (clientId: number) => string;
}

// Cost and profit: what the business pays and keeps. Hidden from an office administrator.
const INTERNAL_FINANCIAL_COLUMNS = new Set(['costTotal', 'totalProfit']);

// What the client is charged. `totalProfit` is in both sets because it is derived
// from the price as well as the cost, so hiding the price alone would still publish it.
const CLIENT_PRICING_COLUMNS = new Set(['priceTotal', 'discountPercent', 'finalPrice', 'totalProfit', 'payment']);

export const getVisitTableColumns = (
  t: TFunction,
  {
    onClientClick,
    onCaregiverClick,
    onVisitClick,
    showInternalFinancials,
    showClientPricing,
    clientCardHref = clientCardPath,
  }: VisitTableNavigationHandlers
): BaseColumn<VisitRow>[] => {
  const columns: BaseColumn<VisitRow>[] = [
    {
      id: 'client',
      header: t('visits-table-col-patient'),
      accessor: (row) => (row.client ? `${row.client.firstName ?? ''} ${row.client.lastName ?? ''}`.trim() : ''),
      width: 180,
      cellRenderer: (_, visit) =>
        visit?.client ? (
          <TableLink to={clientCardHref(visit.client!.clientId)} onClick={() => onClientClick(visit.client!.clientId)}>
            <ClientDisplay firstName={visit.client.firstName || ''} lastName={visit.client.lastName || ''} />
          </TableLink>
        ) : (
          '-'
        ),
    },
    {
      id: 'caregivers',
      header: t('visits-table-col-caregivers'),
      accessor: (row) =>
        row.caregivers ? row.caregivers.map((c) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()).join(', ') : '',
      width: 200,
      cellRenderer: (_, visit) => (
        <>
          {visit?.caregivers?.length
            ? visit.caregivers.map((c) => (
                <TableLink
                  key={`${Math.random().toString()}-${c.caregiverId}`}
                  to={`/${APP_PREFIX}/${CAREGIVERS_PREFIX}/${EDIT_CAREGIVER.replace(':id', String(c.caregiverId))}`}
                  onClick={() => onCaregiverClick(c.caregiverId)}
                >
                  <ClientDisplay firstName={c.firstName || ''} lastName={c.lastName || ''} />
                </TableLink>
              ))
            : '-'}
        </>
      ),
    },
    {
      id: 'startTime',
      header: t('visits-table-col-start-date'),
      accessor: 'startTime',
      align: 'left',
      width: 180,
      sortable: true,
      sortField: 'start_time',
      cellRenderer: (_, visit) => (
        <TableLink
          to={`/${APP_PREFIX}/${VISITS_PAGE}/${visit.id}/edit`}
          onClick={() => onVisitClick(visit.id)}
          title={t('visits-edit-time-tooltip')}
        >
          <DateTimeDisplay
            startTime={visit?.startTime}
            showDateSeparately={false}
            dateFormat={LIST_DATE_FORMAT}
            timeFormat={LIST_TIME_FORMAT}
            timeClassName="text-gray-600"
            isLink={true}
          />
        </TableLink>
      ),
    },
    {
      id: 'endTime',
      header: t('visits-table-col-end-date'),
      accessor: 'endTime',
      align: 'left',
      width: 180,
      sortable: true,
      sortField: 'end_time',
      cellRenderer: (_, visit) => (
        <TableLink
          to={`/${APP_PREFIX}/${VISITS_PAGE}/${visit.id}/edit`}
          onClick={() => onVisitClick(visit.id)}
          title={t('visits-edit-time-tooltip')}
        >
          <DateTimeDisplay
            startTime={visit?.endTime}
            showDateSeparately={false}
            dateFormat={LIST_DATE_FORMAT}
            timeFormat={LIST_TIME_FORMAT}
            timeClassName="text-gray-600"
            isLink={true}
          />
        </TableLink>
      ),
    },
    {
      id: 'costTotal',
      header: t('visits-table-col-total-cost'),
      accessor: 'costTotal',
      align: 'right',
      width: 210,
      sortable: true,
      sortField: 'cost_total',
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit.costTotal === undefined || visit.costTotal === null ? '-' : currencyFormatter(visit.costTotal)}
        </Typography.Text>
      ),
      aggregation: {
        enabled: true,
        field: 'costTotal',
        operations: ['sum', 'avg', 'min', 'max'],
        defaultOperation: 'sum',
        formatValue: (value) => (
          <Typography.Text strong className="text-blue-600">
            {currencyFormatter(Number(value))}
          </Typography.Text>
        ),
      },
    },
    {
      id: 'priceTotal',
      header: t('visits-table-col-total-price'),
      accessor: 'priceTotal',
      align: 'right',
      width: 210,
      sortable: true,
      sortField: 'price_total',
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit.priceTotal === undefined || visit.priceTotal === null ? '-' : currencyFormatter(visit.priceTotal)}
        </Typography.Text>
      ),
      aggregation: {
        enabled: true,
        field: 'priceTotal',
        operations: ['sum', 'avg', 'min', 'max'],
        defaultOperation: 'sum',
        formatValue: (value) => (
          <Typography.Text strong className="text-green-600">
            {currencyFormatter(Number(value))}
          </Typography.Text>
        ),
      },
    },
    {
      id: 'discountPercent',
      header: t('visits-table-col-discount'),
      accessor: 'discountPercent',
      align: 'right',
      width: 150,
      sortable: true,
      sortField: 'discount_percent',
      isVisible: true,
      cellRenderer: (discount) => (
        <Typography.Text>{discount ? `${formatPercent(Number(discount))}%` : '0'}</Typography.Text>
      ),
      aggregation: {
        enabled: true,
        field: 'discountPercent',
        operations: ['sum', 'avg', 'min', 'max'],
        defaultOperation: 'avg',
        formatValue: (value) => (
          <Typography.Text className="text-gray-600">{Number(value).toFixed(1)}%</Typography.Text>
        ),
      },
    },
    {
      id: 'finalPrice',
      // Price after discount. It needs `priceTotal` and `discountPercent` and NOTHING
      // else — in particular not `costTotal`, which the pricing-visibility middleware
      // strips for an office administrator. Requiring it here blanked this column for
      // exactly the role the internal/client-facing split is meant to keep serving.
      header: t('visits-table-col-final-price'),
      accessor: (row) => {
        if (row.priceTotal == undefined) return null;
        return row.discountPercent === 0 || row.discountPercent == undefined
          ? row.priceTotal
          : row.priceTotal * (1 - row.discountPercent / 100);
      },
      align: 'right',
      width: 210,
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit.priceTotal == undefined
            ? '-'
            : currencyFormatter(
                visit.discountPercent === 0 || visit.discountPercent == undefined
                  ? visit.priceTotal
                  : visit.priceTotal * (1 - visit.discountPercent / 100)
              )}
        </Typography.Text>
      ),
      aggregation: {
        enabled: true,
        field: 'priceFinal',
        operations: ['sum', 'avg', 'min', 'max'],
        defaultOperation: 'sum',
        formatValue: (value) => (
          <Typography.Text strong className="text-purple-600">
            {currencyFormatter(Number(value))}
          </Typography.Text>
        ),
      },
    },
    {
      id: 'totalProfit',
      header: t('visits-table-col-total-profit'),
      accessor: (row) => {
        if (row.priceTotal == undefined || row.costTotal == undefined) return null;
        return row.discountPercent === 0 || row.discountPercent == undefined
          ? row.priceTotal - row.costTotal
          : row.priceTotal * (1 - row.discountPercent / 100) - row.costTotal;
      },
      align: 'right',
      width: 200,
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit.priceTotal == undefined || visit.costTotal == undefined
            ? '-'
            : currencyFormatter(
                visit.discountPercent === 0 || visit.discountPercent == undefined
                  ? visit.priceTotal - visit.costTotal
                  : visit.priceTotal * (1 - visit.discountPercent / 100) - visit.costTotal
              )}
        </Typography.Text>
      ),
      aggregation: {
        enabled: true,
        field: 'totalProfit',
        operations: ['sum', 'avg', 'min', 'max'],
        defaultOperation: 'sum',
        formatValue: (value) => (
          <Typography.Text strong className="text-orange-600">
            {currencyFormatter(Number(value))}
          </Typography.Text>
        ),
      },
    },
    {
      id: 'payment',
      header: t('visits-label-payment'),
      accessor: (row) => formatVisitPayment(row.paymentContext, t),
      align: 'left',
      width: 200,
      sortable: false,
      isVisible: true,
      cellRenderer: (_, visit) => {
        const status = getVisitPaymentStatus(visit.paymentContext);
        return (
          <Typography.Text type={status === 'none' || status === 'paid' ? 'secondary' : undefined}>
            {formatVisitPayment(visit.paymentContext, t)}
          </Typography.Text>
        );
      },
    },
    {
      id: 'status',
      header: t('visits-table-col-status'),
      accessor: 'status',
      align: 'center',
      width: 120,
      sortable: true,
      sortField: 'status',
      cellRenderer: (status) => (
        <Tag color={getStatusColor(status as string)}>
          {t(visitStatusLabelKey(status as string), {
            defaultValue: ((status as string) || '').replace('_', ' ').toUpperCase(),
          })}
        </Tag>
      ),
    },
    {
      id: 'services',
      header: t('visits-table-col-services'),
      accessor: 'services',
      align: 'left',
      width: 200,
      keyForCopy: 'name',
      cellRenderer: (_, visit) => (
        <ServicesDisplay
          services={
            visit?.services?.map((service) => ({
              id: service.id,
              name: service?.service?.name || 'Service',
            })) || []
          }
        />
      ),
    },
    {
      id: 'address',
      header: t('visits-table-col-address'),
      accessor: (row) =>
        getAddressWithFallback(
          {
            street: row?.street || undefined,
            city: row?.city || undefined,
            zip: row?.postalCode || undefined,
            floor: row?.floor,
          },
          row?.client
            ? {
                street: row.client.street || undefined,
                city: row.client.city || undefined,
                zip: row.client.postalCode || undefined,
                floor: row.client.floor,
              }
            : undefined,
          t('clients-field-floor')
        ),
      align: 'center',
      width: 250,
      cellRenderer: (_, visit) => {
        const visitAddress = {
          street: visit?.street || undefined,
          city: visit?.city || undefined,
          zip: visit?.postalCode || undefined,
          floor: visit?.floor,
        };
        const clientAddress = visit?.client
          ? {
              street: visit.client.street || undefined,
              city: visit.client.city || undefined,
              zip: visit.client.postalCode || undefined,
              floor: visit.client.floor,
            }
          : undefined;
        const formattedAddress = getAddressWithFallback(visitAddress, clientAddress, t('clients-field-floor'));
        return <Typography.Text ellipsis>{formattedAddress}</Typography.Text>;
      },
    },
    {
      id: 'completedAt',
      header: t('visits-table-col-completed-at'),
      accessor: 'completedAt',
      align: 'left',
      width: 180,
      sortable: true,
      sortField: 'completed_at',
      isVisible: false,
      cellRenderer: (_, visit) =>
        visit.completedAt ? (
          <DateTimeDisplay
            startTime={visit.completedAt}
            showDateSeparately={false}
            dateFormat={LIST_DATE_FORMAT}
            timeFormat={LIST_TIME_FORMAT}
            timeClassName="text-gray-600"
          />
        ) : (
          <Typography.Text>-</Typography.Text>
        ),
    },
    {
      id: 'cancelDate',
      header: t('visits-table-col-cancelled-at'),
      accessor: 'cancelDate',
      align: 'left',
      width: 180,
      sortable: true,
      sortField: 'cancel_date',
      isVisible: false,
      cellRenderer: (_, visit) =>
        visit.cancelDate ? (
          <DateTimeDisplay
            startTime={visit.cancelDate}
            showDateSeparately={false}
            dateFormat={LIST_DATE_FORMAT}
            timeFormat={LIST_TIME_FORMAT}
            timeClassName="text-gray-600"
          />
        ) : (
          <Typography.Text>-</Typography.Text>
        ),
    },
    {
      id: 'heartRate',
      header: t('visits-table-col-heart-rate'),
      accessor: 'heartRate',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'heart_rate',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} bpm` : '-'}</Typography.Text>,
    },
    {
      id: 'bloodPressureSystolic',
      header: t('visits-table-col-bp-systolic'),
      accessor: 'bloodPressureSystolic',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'blood_pressure_systolic',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} mmHg` : '-'}</Typography.Text>,
    },
    {
      id: 'bloodPressureDiastolic',
      header: t('visits-table-col-bp-diastolic'),
      accessor: 'bloodPressureDiastolic',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'blood_pressure_diastolic',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} mmHg` : '-'}</Typography.Text>,
    },
    {
      id: 'temperature',
      header: t('visits-table-col-temperature'),
      accessor: 'temperature',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'temperature',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value}°C` : '-'}</Typography.Text>,
    },
    {
      id: 'oxygenSaturation',
      header: t('visits-table-col-o2-saturation'),
      accessor: 'oxygenSaturation',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'oxygen_saturation',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value}%` : '-'}</Typography.Text>,
    },
    {
      id: 'bloodGlucose',
      header: t('visits-table-col-blood-glucose'),
      accessor: 'bloodGlucose',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'blood_glucose',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} mg/dL` : '-'}</Typography.Text>,
    },
    {
      id: 'fvc',
      header: t('visits-vital-fvc'),
      accessor: 'fvc',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'fvc',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} L` : '-'}</Typography.Text>,
    },
    {
      id: 'fev1',
      header: t('visits-vital-fev1'),
      accessor: 'fev1',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'fev1',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value} L` : '-'}</Typography.Text>,
    },
    {
      // Derived from the two above by a generated column, so it is read-only here as
      // every column in this list is, and it is sorted by the database rather than
      // recomputed per page.
      id: 'fev1FvcRatio',
      header: t('visits-vital-fev1-fvc-ratio'),
      accessor: 'fev1FvcRatio',
      align: 'right',
      width: 120,
      sortable: true,
      sortField: 'fev1_fvc_ratio',
      isVisible: false,
      cellRenderer: (value) => <Typography.Text>{value ? `${value}%` : '-'}</Typography.Text>,
    },
    {
      id: 'referringDoctor',
      header: t('visits-table-col-referring-doctor'),
      accessor: (row) =>
        row?.referringDoctor
          ? `${row.referringDoctor.firstName || ''} ${row.referringDoctor.lastName || ''}`.trim()
          : '',
      width: 180,
      sortable: false,
      isVisible: false,
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit?.referringDoctor ? `${visit.referringDoctor.firstName} ${visit.referringDoctor.lastName}` : '-'}
        </Typography.Text>
      ),
    },
    {
      id: 'attendingDoctor',
      header: t('visits-table-col-attending-doctor'),
      accessor: (row) =>
        row?.attendingDoctor
          ? `${row.attendingDoctor.firstName || ''} ${row.attendingDoctor.lastName || ''}`.trim()
          : '',
      width: 180,
      sortable: false,
      isVisible: false,
      cellRenderer: (_, visit) => (
        <Typography.Text>
          {visit?.attendingDoctor ? `${visit.attendingDoctor.firstName} ${visit.attendingDoctor.lastName}` : '-'}
        </Typography.Text>
      ),
    },
    createdAtColumn<VisitRow>(t),
  ];

  return columns.filter(
    (column) =>
      (showInternalFinancials || !INTERNAL_FINANCIAL_COLUMNS.has(column.id)) &&
      (showClientPricing || !CLIENT_PRICING_COLUMNS.has(column.id)) &&
      (column.id !== 'payment' || envConfig.getEnvKey('PAYMENT_TERMS_ENABLED') === true)
  );
};
